export type PricingModel = 'FREE' | 'FREEMIUM' | 'PAID';

export interface JetBrainsPlugin {
  id: number;
  xmlId: string;
  link: string;
  name: string;
  preview: string;
  downloads: number;
  pricingModel: PricingModel;
  icon: string;
  tags: string[];
}

export const JETBRAINS_VENDOR_PLUGINS_ENDPOINT =
  '/api/jetbrains/vendors/jakub-jirak/plugins?families=intellij,teamcity,hub,fleet,dotnet,space,toolbox,youtrack,teamcity_recipes&page=1&size=30';

export const curatedJetBrainsPlugins: JetBrainsPlugin[] = [
  {
    id: 29001,
    xmlId: 'com.jirakj.vulnspeed',
    link: '/plugin/29001-vulnspeed--gpu-accelerated-sast',
    name: 'VulnSpeed - GPU-Accelerated SAST',
    preview: 'GPU-backed static analysis for teams that need fast security feedback inside JetBrains IDEs.',
    downloads: 1250,
    pricingModel: 'FREEMIUM',
    icon: '',
    tags: ['Security', 'SAST', 'GPU']
  },
  {
    id: 29008,
    xmlId: 'com.jirakj.ansi-log-viewer',
    link: '/plugin/29008-ansi-log-viewer',
    name: 'ANSI Log Viewer',
    preview: 'Readable ANSI and truecolor logs directly in the editor for faster debugging and support work.',
    downloads: 1180,
    pricingModel: 'FREE',
    icon: '',
    tags: ['Logs', 'DevOps', 'Observability']
  },
  {
    id: 28974,
    xmlId: 'com.jirakj.accessibility-compliance-helper-pro',
    link: '/plugin/28974-accessibility-compliance-helper-pro',
    name: 'Accessibility Compliance Helper Pro',
    preview: 'Practical WCAG checks, remediation guidance, and accessibility workflows inside JetBrains IDEs.',
    downloads: 1100,
    pricingModel: 'FREEMIUM',
    icon: '',
    tags: ['Accessibility', 'WCAG', 'QA']
  },
  {
    id: 29038,
    xmlId: 'com.jirakj.jetbrains-marketplace-widget',
    link: '/plugin/29038',
    name: 'JetBrains Marketplace Widget',
    preview: 'A lightweight widget for showcasing JetBrains Marketplace plugins on product and portfolio sites.',
    downloads: 980,
    pricingModel: 'FREE',
    icon: '',
    tags: ['Marketplace', 'Widget', 'Portfolio']
  },
  {
    id: 29042,
    xmlId: 'com.jirakj.bdd-test-automation-pro',
    link: '/plugin/29042-bdd-test-automation-pro',
    name: 'BDD Test Automation Pro',
    preview: 'Cucumber and Gherkin support for teams building maintainable behavior-driven test suites.',
    downloads: 1400,
    pricingModel: 'FREEMIUM',
    icon: '',
    tags: ['BDD', 'Testing', 'Automation']
  },
  {
    id: 29044,
    xmlId: 'com.jirakj.gpu-dev-accelerator-ultimate',
    link: '/plugin/29044-gpu-dev-accelerator-ultimate',
    name: 'GPU Dev Accelerator Ultimate',
    preview: 'GPU-focused development support for high-performance engineering workflows and large projects.',
    downloads: 1320,
    pricingModel: 'PAID',
    icon: '',
    tags: ['GPU', 'Performance', 'HPC']
  },
  {
    id: 29045,
    xmlId: 'com.jirakj.intel-dpc-support',
    link: '/plugin/29045-intel-dpc--support',
    name: 'Intel DPC++ Support',
    preview: 'Productive Intel DPC++ and SYCL development support for accelerator programming.',
    downloads: 1040,
    pricingModel: 'FREE',
    icon: '',
    tags: ['DPC++', 'SYCL', 'oneAPI']
  },
  {
    id: 29046,
    xmlId: 'com.jirakj.robot-framework-professional',
    link: '/plugin/29046-robot-framework-professional',
    name: 'Robot Framework Professional',
    preview: 'Professional Robot Framework tooling for test automation teams working in JetBrains IDEs.',
    downloads: 1600,
    pricingModel: 'FREEMIUM',
    icon: '',
    tags: ['Robot Framework', 'QA', 'Testing']
  },
  {
    id: 29049,
    xmlId: 'com.jirakj.seleniumiq',
    link: '/plugin/29049-seleniumiq--ai-powered-test-automation',
    name: 'SeleniumIQ - AI-Powered Test Automation',
    preview: 'AI-assisted Selenium workflows for faster test creation, maintenance, and coverage analysis.',
    downloads: 1500,
    pricingModel: 'FREEMIUM',
    icon: '',
    tags: ['Selenium', 'AI', 'Testing']
  },
  {
    id: 29050,
    xmlId: 'com.jirakj.logparser-pro',
    link: '/plugin/29050-logparser-pro',
    name: 'LogParser Pro',
    preview: 'Fast log parsing, search, and reporting for developers working with production-grade logs.',
    downloads: 1220,
    pricingModel: 'PAID',
    icon: '',
    tags: ['Logs', 'Parsing', 'Tools']
  }
];

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const toStringValue = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);

const toNumberValue = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const normalizePricingModel = (value: unknown): PricingModel => {
  const normalized = toStringValue(value).toUpperCase();

  if (normalized.includes('FREEMIUM')) {
    return 'FREEMIUM';
  }

  if (normalized.includes('PAID') || normalized.includes('COMMERCIAL')) {
    return 'PAID';
  }

  return 'FREE';
};

export const normalizeMarketplacePlugins = (payload: unknown): JetBrainsPlugin[] => {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.reduce<JetBrainsPlugin[]>((plugins, item) => {
    const record = toRecord(item);
    if (!record) {
      return plugins;
    }

    const id = toNumberValue(record.id);
    const name = toStringValue(record.name);

    if (!id || !name) {
      return plugins;
    }

    const rawLink = toStringValue(record.link);
    const link = rawLink.startsWith('/plugin/') ? rawLink : `/plugin/${id}`;
    const rawTags = Array.isArray(record.tags) ? record.tags : [];

    plugins.push({
      id,
      xmlId: toStringValue(record.xmlId) || `plugin-${id}`,
      link,
      name,
      preview: toStringValue(record.preview) || 'Professional JetBrains IDE tooling by Jakub Jirak.',
      downloads: toNumberValue(record.downloads),
      pricingModel: normalizePricingModel(record.pricingModel),
      icon: toStringValue(record.icon),
      tags: rawTags.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0)
    });

    return plugins;
  }, []);
};
